/**
 * Minimal name → MLB team ID mapping for logo resolution.
 * Current MLB teams plus common short names and franchise aliases
 * used in historical scraped data. Unmapped names return null.
 */

const CURRENT_TEAMS_BY_NAME = {
  "los angeles angels": 108,
  "arizona diamondbacks": 109,
  "baltimore orioles": 110,
  "boston red sox": 111,
  "chicago cubs": 112,
  "cincinnati reds": 113,
  "cleveland guardians": 114,
  "cleveland indians": 114,
  "colorado rockies": 115,
  "detroit tigers": 116,
  "houston astros": 117,
  "kansas city royals": 118,
  "los angeles dodgers": 119,
  "washington nationals": 120,
  "new york mets": 121,
  "oakland athletics": 133,
  athletics: 133,
  "pittsburgh pirates": 134,
  "san diego padres": 135,
  "seattle mariners": 136,
  "san francisco giants": 137,
  "st. louis cardinals": 138,
  "st louis cardinals": 138,
  "tampa bay rays": 139,
  "texas rangers": 140,
  "toronto blue jays": 141,
  "minnesota twins": 142,
  "philadelphia phillies": 143,
  "atlanta braves": 144,
  "chicago white sox": 145,
  "miami marlins": 146,
  "florida marlins": 146,
  "new york yankees": 147,
  "milwaukee brewers": 158,
};

/** Short names and aliases from scraped/historical team records */
const TEAM_ALIASES = {
  angels: 108,
  diamondbacks: 109,
  dbacks: 109,
  orioles: 110,
  "red sox": 111,
  cubs: 112,
  reds: 113,
  guardians: 114,
  indians: 114,
  rockies: 115,
  tigers: 116,
  astros: 117,
  royals: 118,
  dodgers: 119,
  nationals: 120,
  mets: 121,
  pirates: 134,
  padres: 135,
  mariners: 136,
  giants: 137,
  cardinals: 138,
  rays: 139,
  rangers: 140,
  "blue jays": 141,
  twins: 142,
  phillies: 143,
  braves: 144,
  "white sox": 145,
  marlins: 146,
  yankees: 147,
  brewers: 158,
  // Franchise continuations where a modern logo is reasonable
  beaneaters: 144,
  doves: 144,
  bridegrooms: 119,
  colts: 114,
  "brooklyn dodgers": 119,
};

const TEAM_ABBREVIATIONS = {
  laa: 108,
  az: 109,
  ari: 109,
  bal: 110,
  bos: 111,
  chc: 112,
  cin: 113,
  cle: 114,
  col: 115,
  det: 116,
  hou: 117,
  kc: 118,
  lad: 119,
  wsh: 120,
  nym: 121,
  ath: 133,
  oak: 133,
  pit: 134,
  sd: 135,
  sea: 136,
  sf: 137,
  stl: 138,
  tb: 139,
  tex: 140,
  tor: 141,
  min: 142,
  phi: 143,
  atl: 144,
  cws: 145,
  mia: 146,
  nyy: 147,
  mil: 158,
};

const normalizeTeamKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * Resolve a team name, abbreviation, or numeric ID to an MLB team ID.
 * Returns null when no mapping exists (historical/defunct teams).
 */
const titleCaseTeam = (name) =>
  String(name)
    .split(" ")
    .map((part) =>
      part
        .split("-")
        .map((chunk) =>
          chunk ? chunk[0].toUpperCase() + chunk.slice(1) : chunk
        )
        .join("-")
    )
    .join(" ");

export const listCurrentMlbTeams = () => {
  const seen = new Set();
  return Object.entries(CURRENT_TEAMS_BY_NAME)
    .filter(([name]) => name.includes(" "))
    .flatMap(([name, id]) => {
      if (seen.has(id)) return [];
      seen.add(id);
      return [{ id, teamId: titleCaseTeam(name), name: titleCaseTeam(name) }];
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const resolveMlbTeamId = (teamNameOrId) => {
  if (teamNameOrId == null || teamNameOrId === "") return null;

  const numericId = Number(teamNameOrId);
  if (Number.isInteger(numericId) && numericId > 0) {
    return numericId;
  }

  const key = normalizeTeamKey(teamNameOrId);

  return (
    CURRENT_TEAMS_BY_NAME[key] ??
    TEAM_ALIASES[key] ??
    TEAM_ABBREVIATIONS[key] ??
    null
  );
};

export default resolveMlbTeamId;
